import { useRef, useState } from "react";
import * as htmlToImage from "html-to-image";
import { Loader2, Share2, Copy, Download, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { categoryLabel } from "@/components/CategoryBadge";

const CATEGORY_TINT = {
  psychology: "#4b5b6f",
  sociology: "#6c5836",
  philosophy: "#5f4628",
  human_behavior: "#3d5530",
  relationships: "#874030",
};

/**
 * ShareButton — small pill button that opens a Share dialog for one card.
 *
 * Tries: navigator.share (mobile), copy to clipboard, download as PNG.
 */
export default function ShareButton({ term, size = "md", className = "" }) {
  const [open, setOpen] = useState(false);

  if (!term) return null;

  const small = size === "sm";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="share-btn"
        aria-label="Share this card"
        className={`inline-flex items-center gap-1.5 rounded-full font-medium transition-colors ${
          small
            ? "h-8 px-3 text-xs bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
            : "h-10 px-4 text-sm bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
        } ${className}`}
      >
        <Share2 className={small ? "w-3.5 h-3.5" : "w-4 h-4"} strokeWidth={1.8} />
        Share
      </button>
      {open && <ShareDialog term={term} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareDialog({ term, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'image' | 'copy' | 'native'
  const [copied, setCopied] = useState(false);

  const shareText = `${term.term}\n\n${term.meaning}\n\n— InThink`;
  const shareUrl = `${window.location.origin}/term/${term.id}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const renderImage = async () => {
    const node = cardRef.current;
    if (!node) throw new Error("Card not ready");
    return htmlToImage.toPng(node, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: "#FBF6E4",
    });
  };

  const tryNativeShare = async () => {
    setBusy("native");
    try {
      try {
        const dataUrl = await renderImage();
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `${slugify(term.term)}.png`, { type: "image/png" });
        if (canNativeShare && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: "InThink", text: shareText, files: [file] });
          toast.success("Shared");
          return;
        }
      } catch (_e) {
        // Fall through to text share
      }
      if (canNativeShare) {
        await navigator.share({ title: term.term, text: shareText, url: shareUrl });
        toast.success("Shared");
      } else {
        await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
        toast.success("Copied to clipboard");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Couldn't share");
      }
    } finally {
      setBusy(null);
    }
  };

  const copyText = async () => {
    setBusy("copy");
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy");
    } finally {
      setBusy(null);
    }
  };

  const downloadImage = async () => {
    setBusy("image");
    try {
      const dataUrl = await renderImage();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `inthink-${slugify(term.term)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Image downloaded");
    } catch {
      toast.error("Couldn't render image");
    } finally {
      setBusy(null);
    }
  };

  const tint = CATEGORY_TINT[term.category] || "#4b5b6f";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-[var(--inmind-bg)]" data-testid="share-dialog">
        <DialogHeader className="px-5 py-4 border-b border-black/5 flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <DialogTitle className="font-serif text-lg text-[var(--inmind-text)]">Share this card</DialogTitle>
            <DialogDescription className="text-xs text-[var(--inmind-text-secondary)]">
              Send it to a friend or save it for later.
            </DialogDescription>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-full text-[var(--inmind-muted)] hover:bg-black/5"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {/* Preview that we render into an image */}
          <div className="rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
            <div
              ref={cardRef}
              data-testid="share-card-preview"
              style={{
                background: "#FBF6E4",
                padding: "32px",
                fontFamily: "'Manrope', sans-serif",
                color: "#2C302E",
              }}
            >
              <div style={{
                fontSize: "10px", letterSpacing: "0.22em", textTransform: "uppercase",
                fontWeight: 700, color: tint, marginBottom: "12px",
              }}>
                {categoryLabel(term.category)} · InThink
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                fontSize: "30px", lineHeight: "1.15", marginBottom: "16px",
                color: "#2C302E", fontWeight: 500,
              }}>
                {term.term}
              </div>
              <div style={{ fontSize: "14px", lineHeight: "1.6", marginBottom: "20px" }}>
                {term.meaning}
              </div>
              <div style={{
                borderTop: "1px solid rgba(0,0,0,0.08)",
                paddingTop: "14px", fontSize: "12px",
                lineHeight: "1.55", color: "#5a5e5c",
              }}>
                <span style={{
                  fontSize: "9px", letterSpacing: "0.22em",
                  textTransform: "uppercase", fontWeight: 700,
                  color: "#8A8D8B", display: "block", marginBottom: "6px",
                }}>
                  Did you know
                </span>
                {term.fact}
              </div>
              <div style={{
                marginTop: "20px",
                fontSize: "10px", letterSpacing: "0.18em",
                textTransform: "uppercase", fontWeight: 600,
                color: "#8A8D8B",
              }}>
                inthink.world
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {canNativeShare ? (
              <Button
                onClick={tryNativeShare}
                disabled={!!busy}
                data-testid="share-native"
                className="rounded-xl h-10 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] gap-1.5"
              >
                {busy === "native" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" strokeWidth={1.8} />}
                Share
              </Button>
            ) : (
              <Button
                onClick={copyText}
                disabled={!!busy}
                data-testid="share-copy-primary"
                className="rounded-xl h-10 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] gap-1.5"
              >
                {copied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.8} />}
                Copy
              </Button>
            )}
            <Button
              onClick={copyText}
              disabled={!!busy}
              data-testid="share-copy"
              variant="ghost"
              className={`rounded-xl h-10 border border-black/10 hover:bg-black/5 gap-1.5 ${canNativeShare ? "" : "hidden sm:inline-flex"}`}
            >
              {copied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.8} />}
              {copied ? "Copied" : "Copy text"}
            </Button>
            <Button
              onClick={downloadImage}
              disabled={!!busy}
              data-testid="share-image"
              variant="ghost"
              className="rounded-xl h-10 border border-black/10 hover:bg-black/5 gap-1.5"
            >
              {busy === "image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.8} />}
              Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function slugify(s) {
  return (s || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}
